import React from 'react';
import {
  Platform,
  View,
  Text,
  ScrollView,
  Switch,
  TextInput as RNTextInput,
} from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import ConfirmDiscardModal from '../../../../components/ConfirmDiscardModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import LHOverlay from '../../../../components/LHOverlay';
import LhTextInput from '../../../../components/LhTextInput';
import LhButton from '../../../../components/LhButton';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  NHO_TOUCH_TYPE_OPTIONS,
  NHO_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import LHDropDown from '../../../../components/LHDropDown';
import KfaMultiSelectDropdown from './KfaMultiSelectDropdown';
import NhoSearchableDropdown from './NhoSearchableDropdown';
import { useNhoForm } from './useNhoForm';
import { styles } from './NhoAddOverlay.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Full dataset for edit mode — mirrors expected API response fields */
export interface NhoEditItem {
  id: string;
  qtext: string;
  sectionType?: string;
  touchTypes?: string[];
  preferredMethod?: number;
  sectionHeader?: string;
  sectionSubHeader?: string;
  organizationType?: string;
  locationType?: string;
  category?: string;
  /** "YES" | "NO" */
  threePlStatus?: string;
  status?: boolean;
  section?: number;
  sortOrder?: number;
  /** ISO timestamp — used to enforce the 24-hour edit lock on text fields */
  createdAt?: string;
}

interface NhoAddOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  mode?: 'create' | 'edit';
  selectedItem?: NhoEditItem;
  onSaveSuccess?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const NhoAddOverlay: React.FC<NhoAddOverlayProps> = ({
  visible,
  onDismiss,
  mode = 'create',
  selectedItem,
  onSaveSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const {
    // save state
    isSaving, saveError,
    // form fields
    touchTypes, setTouchTypes,
    section, sectionId,
    sectionNumber, setSectionNumber,
    sortOrder, setSortOrder,
    qtext, setQtext,
    selectedHeader, setSelectedHeader,
    selectedSubHeader,
    locationType, setLocationType,
    organizationType, setOrganizationType,
    threeplStatus, setThreeplStatus,
    category, setCategory,
    statusEnabled, setStatusEnabled,
    isTextLocked,
    // validation
    validationMessage, fieldErrors,
    isDirtyRef,
    showDiscardConfirm, setShowDiscardConfirm,
    // derived
    assessmentLabel, showSubHeaderDropdown, showHeaderDropdown, sideBySideLayout,
    headerOptions, subHeaderOptions, headersLoaded,
    // handlers
    handleSave, handleCancel, handleSectionChange, handleSubHeaderChange, clearFieldError,
  } = useNhoForm({ visible, mode, selectedItem, onDismiss, onSaveSuccess });

  return (
    <>
      <ConfirmDiscardModal
        visible={showDiscardConfirm}
        onKeepEditing={() => setShowDiscardConfirm(false)}
        onDiscard={() => { setShowDiscardConfirm(false); onDismiss(); }}
      />
      <LHOverlay
        visible={visible}
        onDismiss={handleCancel}
        title={mode === 'edit' ? 'Edit NHO Question' : 'Add NHO Question'}
        size="medium"
        position="center"
        close={true}
      >
        <View style={styles.overlayWrapper}>
          {validationMessage ? (
            <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
              <MaterialIcons name="error-outline" size={16} color={isDark ? colors.status.error : '#C62828'} style={{ marginRight: 8 }} />
              <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{validationMessage}</Text>
            </View>
          ) : null}
          {saveError ? (
            <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
              <MaterialIcons name="error-outline" size={16} color={isDark ? colors.status.error : '#C62828'} style={{ marginRight: 8 }} />
              <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{saveError}</Text>
            </View>
          ) : null}

          <ScrollView style={styles.scrollContent} contentContainerStyle={styles.overlayContent}>

            {/* Status Toggle — edit mode only */}
            {mode === 'edit' && (
              <View style={styles.statusRow}>
                <Text style={[styles.statusLabel, isDark && { color: colors.text.primary }]}>Status:</Text>
                <Switch
                  value={statusEnabled}
                  onValueChange={(val) => {
                    isDirtyRef.current = true;
                    setStatusEnabled(val);
                  }}
                  thumbColor="#FFFFFF"
                  trackColor={{ false: '#CCCCCC', true: '#2E7D32' }}
                />
                <Text style={[styles.statusValue, { color: statusEnabled ? '#2E7D32' : '#757575' }]}>
                  {statusEnabled ? 'Enabled' : 'Disabled'}
                </Text>
              </View>
            )}

            {/* ── 1. Module Assignment ─────────────────────────────────────────── */}
            <View style={styles.moduleAssignmentSection}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Module Assignment</Text>
              </View>

              {/* Row: Touch Type (multi) + Section (single) */}
              <View style={[styles.dropdownRow, { zIndex: 30 }]}>
                <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.touchTypes && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Touch Type</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <KfaMultiSelectDropdown
                    label=""
                    options={NHO_TOUCH_TYPE_OPTIONS}
                    selected={touchTypes}
                    onChange={(values) => {
                      setTouchTypes(values);
                      clearFieldError('touchTypes');
                    }}
                    placeholder="Select Touch Type(s)"
                  />
                </View>

                <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.section && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <LHDropDown
                    label=""
                    childList={NHO_SECTION_OPTIONS}
                    selectedValue={section}
                    setSelectedValue={(newSection: any) => {
                      handleSectionChange(newSection);
                      clearFieldError('section');
                      clearFieldError('header');
                      clearFieldError('subHeader');
                    }}
                    placeholder="Select Section"
                  />
                </View>
              </View>

              {/* Section number + Sort Order */}
              {sectionId !== '' && (
                <View style={[styles.dropdownRow, { marginTop: 8 }]}>
                  {sectionId === 'Header' && (
                    <View style={[styles.dropdownColumn]}>
                        <Text style={[styles.intInputLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                      <RNTextInput
                        style={styles.intInput}
                        value={sectionNumber}
                        onChangeText={(v) => { isDirtyRef.current = true; setSectionNumber(v.replace(/[^0-9]/g, '')); }}
                        placeholder="e.g. 1"
                        placeholderTextColor="#AAAAAA"
                        keyboardType="numeric"
                      />
                    </View>
                  )}
                  <View style={[styles.dropdownColumn]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.intInputLabel, isDark && { color: colors.text.primary }]}>Sort Order</Text>
                      <Text style={styles.requiredLabel}>*Required</Text>
                    </View>
                    <RNTextInput
                      style={[styles.intInput, fieldErrors.sortOrder && styles.inputError]}
                      value={sortOrder}
                      onChangeText={(v) => {
                        setSortOrder(v.replace(/[^0-9]/g, ''));
                        clearFieldError('sortOrder');
                      }}
                      placeholder="e.g. 5"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="numeric"
                    />
                  </View>
                </View>
              )}

              {/* Dynamic Header / Sub-Header dropdowns */}
              {(showSubHeaderDropdown || showHeaderDropdown) && (
                <View style={[sideBySideLayout ? styles.dynamicDropdownRow : null, { zIndex: 40, marginTop: 8 }]}>
                  {showSubHeaderDropdown && (
                    <View
                      style={[
                        sideBySideLayout ? styles.dynamicDropdownHalf : styles.dynamicDropdownFull,
                        { zIndex: 40 },
                        fieldErrors.subHeader && styles.dropdownErrorWrapper,
                      ]}
                    >
                      <View style={styles.labelRow}>
                        <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Sub-Header</Text>
                        <Text style={styles.requiredLabel}>*Required</Text>
                      </View>
                      <NhoSearchableDropdown
                        label=""
                        options={subHeaderOptions}
                        selected={selectedSubHeader}
                        onChange={(opt) => {
                          handleSubHeaderChange(opt);
                          clearFieldError('subHeader');
                          if (opt) clearFieldError('header');
                        }}
                        placeholder={headersLoaded ? 'Search or select Sub-Header...' : 'Loading...'}
                        maxItems={50}
                      />
                    </View>
                  )}

                  {showHeaderDropdown && (
                    <View
                      style={[
                        sideBySideLayout ? styles.dynamicDropdownHalf : styles.dynamicDropdownFull,
                        { zIndex: 40 },
                        fieldErrors.header && styles.dropdownErrorWrapper,
                      ]}
                    >
                      <View style={styles.labelRow}>
                        <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Header</Text>
                        <Text style={styles.requiredLabel}>*Required</Text>
                      </View>
                      <NhoSearchableDropdown
                        label=""
                        options={headerOptions}
                        selected={selectedHeader}
                        onChange={(opt) => {
                          setSelectedHeader(opt);
                          clearFieldError('header');
                        }}
                        placeholder={headersLoaded ? 'Search or select Header...' : 'Loading...'}
                        maxItems={50}
                      />
                    </View>
                  )}
                </View>
              )}
            </View>

            {/* ── 2. Assessment Text ───────────────────────────────────────────── */}
            <View style={styles.assessmentSection}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Assessment Text</Text>
              </View>
              <View style={styles.inputGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                    {assessmentLabel}{' '}
                    {section && (
                      <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>
                        {sectionId === 'Header' && '(section title text)'}
                        {sectionId === 'Sub-Header' && '(sub-header text content)'}
                        {sectionId === 'Data' && '(key focus area statement)'}
                        {sectionId === 'Sub-Data' && '(data row content)'}
                      </Text>
                    )}
                  </Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <LhTextInput
                  value={qtext}
                  onChangeText={(val) => {
                    setQtext(val);
                    clearFieldError('qtext');
                  }}
                  placeholder={
                    sectionId === 'Header' ? 'e.g., Safety Fundamentals'
                    : sectionId === 'Sub-Header' ? 'e.g., Personal Protective Equipment'
                    : sectionId === 'Data' ? 'e.g., Employee is wearing proper PPE'
                    : sectionId === 'Sub-Data' ? 'e.g., Hard hat worn at all times'
                    : 'Enter text...'
                  }
                  multiline
                  numberOfLines={3}
                  editable={!isTextLocked}
                  style={styles.textInput}
                  wrapperStyle={[styles.inputWrapper, fieldErrors.qtext && styles.inputError, isTextLocked && styles.inputLocked]}
                />
                <View style={styles.charCountRow}>
                  {isTextLocked && <Text style={[styles.lockedHelperText, isDark && { color: colors.text.secondary }]}>Locked 24 hours after creation.</Text>}
                  <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{qtext.length}/500</Text>
                </View>
              </View>
            </View>

            {/* ── 3. Shared Attributes ─────────────────────────────────────────── */}
            <View style={styles.sharedAttributesSection}>
              <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Shared Attributes</Text>
              </View>

              {/* Row 1: Location Type + Organization Type */}
              <View style={[styles.dropdownRow, { zIndex: 20 }]}>
                <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.locationType && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Location Type</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <KfaMultiSelectDropdown
                    label=""
                    options={LOCATION_TYPE_OPTIONS}
                    selected={locationType}
                    onChange={(val) => { setLocationType(val); clearFieldError('locationType'); }}
                    placeholder="Select Location Type"
                  />
                </View>
                <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.organizationType && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Organization Type</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <KfaMultiSelectDropdown
                    label=""
                    options={ORGANIZATION_TYPE_OPTIONS}
                    selected={organizationType}
                    onChange={(val) => { setOrganizationType(val); clearFieldError('organizationType'); }}
                    placeholder="Select Organization Type"
                  />
                </View>
              </View>

              {/* Row 2: 3PL Status + Category */}
              <View style={[styles.dropdownRow, { zIndex: 10 }]}>
                <View style={[styles.dropdownColumn, { zIndex: 10 }, fieldErrors.threeplStatus && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>3PL Status</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <LHDropDown
                    label=""
                    childList={THREE_PL_OPTIONS}
                    selectedValue={threeplStatus}
                    setSelectedValue={(val: any) => {
                      setThreeplStatus(val);
                      clearFieldError('threeplStatus');
                    }}
                    placeholder="Select 3PL Status"
                  />
                </View>
                <View style={[styles.dropdownColumn, { zIndex: 10 }, fieldErrors.category && styles.dropdownErrorWrapper]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Category</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <KfaMultiSelectDropdown
                    label=""
                    options={SPARC_CATEGORY_OPTIONS}
                    selected={category}
                    onChange={(val) => { setCategory(val); clearFieldError('category'); }}
                    placeholder="Select Category"
                    singleSelect
                  />
                </View>
              </View>
            </View>

          </ScrollView>

          {/* ── Footer ─────────────────────────────────────────────────────────── */}
          <View style={styles.overlayFooter}>
            <View style={styles.footerButtons}>
              <LhButton text="Cancel" onPress={handleCancel} variant="secondary" disabled={isSaving} />
              <LhButton
                text="Save"
                onPress={handleSave}
                variant="primary"
                isLoading={isSaving}
                disabled={isSaving}
              />
            </View>
          </View>
        </View>
      </LHOverlay>
    </>
  );
};

export default NhoAddOverlay;
